/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Card,
  Modal,
  Input,
  message,
  Typography,
} from 'antd';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import { onError } from 'utils/handlers';
import { getCiudadanoCaja } from 'api/cuentaunicasir/index';
import Uploader from 'components/Uploader';
import { InputSearchPro } from 'components/InputSearch';
import {
  deleteRegistroDeBeneficiario,
  getRegistrosDeBeneficiarios,
  patchRegistroDeBeneficiario,
  postRegistroDeBeneficiario,
} from 'api/recaudacion/registrosDeBeneficiarios';
import { getCiudadano } from 'api/cuentaunicasir';
import {
  CURP_PATTERN, digitoVerificador, INE_PATTERN, RFC_PATTERN,
} from 'utils/patterns';
import { SearchOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import { nameValidationSearch } from 'utils/nameValidationSearch';
import { capitalize } from 'utils/normalizers';
import { DATE_FRONTEND_FORMAT } from 'utils/formatValues';
import LadaNumero from 'components/LadaNumero';
import { getDepositosEnGarantia } from 'api/recaudacion/depositosEnGarantia';
import { getEmpresa, getEmpresas } from 'api/empresas/empresas';
import usePermissionMessage from 'hooks/usePermissionMessage';
import { modelsSearches } from 'utils/permission/models';
import DepositosEnGarantia from './DepositosEnGarantia';
import BusquedaAvanzadaPadrones from '../Caja/BusquedaAvanzadaPadrones';

export const permissionConfig = {
  permissionModel: 'registrodebeneficiarios',
  catalogsModels: [
    'usuario',
    'ciudadano',
    'empresa',
    ...modelsSearches.empresa,
    ...modelsSearches.ciudadano,
  ],
};

// eslint-disable-next-line react/prop-types
const RegistroDeBeneficiarios = ({ permission }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRowKeysCiudadanos, setSelectedRowKeysCiudadanos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [ciudadanoFound, setCiudadanoFound] = useState([]);
  const [visibleBusquedaModal, setVisibleBusquedaModal] = useState(false);
  const [dataCiudadanos, setDataCiudadanos] = useState({ results: [] });
  const [currentPage, setCurrentPage] = useState(1);
  const [visibleBusquedaAvanzada, setVisibleBusquedaAvanzada] = useState(false);
  const [visibleBusquedaAvanzadaEmpresa, setVisibleBusquedaAvanzadaEmpresa] = useState(false);
  const [empresaFound, setEmpresaFound] = useState([]);
  usePermissionMessage(permission);

  const fetchData = async () => {
    setLoading(true);
    const _data = await getRegistrosDeBeneficiarios();
    setData(_data);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      const _data = await getRegistrosDeBeneficiarios();
      if (mounted) {
        setData(_data);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    if (!visibleBusquedaModal) {
      form.resetFields();
      setSelectedRowKeys([]);
      setVisible(false);
      setCiudadanoFound([]);
    } else {
      setVisibleBusquedaModal(false);
      setSelectedRowKeys([]);
      form.resetFields([
        'clave_ciudadana',
        'nombre',
        'apellido_paterno',
        'apellido_materno',
        'email',
        'numero_de_celular',
        'CURP',
        'RFC',
      ]);
    }
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;
      values.nombre_completo = ciudadanoFound[0]?.nombre_completo;
      const saved = await (!key ? postRegistroDeBeneficiario(values)
        : patchRegistroDeBeneficiario(key, values));
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };
  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const handleOnRowClickSearch = (record) => setSelectedRowKeysCiudadanos([record.id]);

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickEdit = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = data.find((e) => e.id === key);
    if (match.ciudadano) {
      const _beneficiario = await getCiudadano(match.ciudadano);
      setCiudadanoFound([_beneficiario]);
    }
    setLoading(false);
    setVisible(true);
  };

  const onClickDelete = () => setVisibleAlert(true);

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const rowSelectionCiudadanos = {
    selectedRowKeys: selectedRowKeysCiudadanos,
    type: 'radio',
  };

  const deleteItem = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = data.find((e) => e.id === key);
    const _depositos = await getDepositosEnGarantia({ registro_de_beneficiario: match.id });
    if (selectedRowKeys.length && !_depositos.length) {
      const deleted = await deleteRegistroDeBeneficiario(key);
      if (deleted) {
        onCancel();
        setVisibleAlert(false);
        await fetchData();
      }
    } else {
      message.warn('No se puede eliminar un registro de beneficiario con movimientos.');
      setVisibleAlert(false);
      setSelectedRowKeys([]);
    }
    setLoading(false);
  };

  const columns = [
    {
      titleText: 'Beneficiario',
      dataIndex: 'nombre_completo',
      key: 'nombre_completo',
      width: 300,
    },
    {
      titleText: 'Observaciones',
      dataIndex: 'observaciones_del_beneficiario',
      key: 'observaciones_del_beneficiario',
      width: 150,
    },
  ];

  const columnsBusqueda = [
    {
      titleText: 'Clave',
      dataIndex: 'clave_ciudadana',
      key: 'clave_ciudadana',
      width: 250,
    },
    {
      titleText: 'Correo',
      dataIndex: 'email',
      key: 'email',
      width: 300,
    },
    {
      titleText: 'Nombre Completo',
      dataIndex: 'nombre_completo',
      key: 'nombre_completo',
      width: 200,
    },
    {
      titleText: 'Celular',
      dataIndex: 'numero_de_celular',
      key: 'numero_de_celular',
      width: 200,
    },
    {
      titleText: 'CURP',
      dataIndex: 'CURP',
      key: 'CURP',
      width: 200,
    },
    {
      titleText: 'RFC',
      dataIndex: 'RFC',
      key: 'RFC',
      width: 200,
    },
    {
      titleText: 'Correo',
      dataIndex: 'email',
      key: 'email',
      width: 300,
    },
    {
      titleText: 'Fecha de Nacimiento',
      dataIndex: 'fecha_nacimiento',
      key: 'fecha_nacimiento',
      width: 300,
      render: (val) => (val?.format(DATE_FRONTEND_FORMAT)),
    },
  ];

  const onAdd = () => {
    const [key] = selectedRowKeysCiudadanos;
    const match = dataCiudadanos.results.find((e) => e.id === key);
    setCiudadanoFound([match]);
    form.setFieldsValue({ ciudadano: match.id });
    onCancel();
  };

  const resetAndValidate = (qName = 'q') => {
    (qName === 'q' ? setCiudadanoFound : setEmpresaFound)([]);
    form.resetFields([qName]);
    form.validateFields([qName]);
  };

  const searchCiudadano = async () => {
    try {
      setLoading(true);
      await form.validateFields(['q']);
      const { q } = form.getFieldsValue(['q']);
      if (q) {
        const _ciudadano = await getCiudadanoCaja({ q });
        const ciudadanosIds = data.map((e) => e.ciudadano);
        const alreadyExists = _ciudadano.results.some((e) => ciudadanosIds.includes(e.id));
        if (_ciudadano.results.length && !alreadyExists) {
          const [selected] = _ciudadano.results;
          form.setFieldsValue({
            ciudadano: selected.id,
          });
          const formatted = {
            ...selected,
            nombre_completo_corto: `${selected?.nombre_completo || ''}`,
          };
          setCiudadanoFound([formatted]);
        } else if (alreadyExists) {
          message.warn('El ciudadano ya se encuentra registrado.');
        } else if (!_ciudadano.results.length) {
          message.info('No se encontró al ciudadano.');
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const rules = {
    email: [{ type: 'email' }],
    RFC: [
      {
        pattern: RFC_PATTERN,
        message: 'Ingrese un RFC válido',
      },
    ],
    CURP: [
      {
        validator: async (rule, value) => {
          if (value) {
            const validado = value.toUpperCase().match(CURP_PATTERN);
            if (!validado || validado[2] !== digitoVerificador(validado[1])) {
              throw new Error('Ingrese una CURP válida');
            }
          }
        },
      },
    ],
    INE: [
      {
        validator: async (rule, value) => {
          if (value) {
            if (!INE_PATTERN.test(value)) {
              throw new Error('Ingrese un INE válido');
            }
          }
        },
      },
    ],
    celular: [
      { required: false },
      {
        pattern: /^(?:[0-9]{10}|)$/,
        message: 'Ingrese un número telefónico válido',
      },
    ],
  };

  const handleSearch = async (page = 1, q = null, ignore = false) => {
    try {
      setLoading(true);
      setCurrentPage(page);
      await form.validateFields();
      setSelectedRowKeys([]);
      const values = form.getFieldsValue();
      const keys = Object.keys(values);
      const validProps = keys.filter((prop) => values[prop]);
      const validationName = nameValidationSearch(validProps);
      if (validProps.length || ignore) {
        if (validationName) {
          const results = await getCiudadanoCaja({
            page,
            ...values,
            ...q,
          });
          if (results?.results?.length || results?.length) {
            setDataCiudadanos(results);
          } else {
            message.warn('No se encontró ningún ciudadano relacionado');
          }
        } else {
          message.warn('Debe ingresar mínimo dos campos referentes a nombre');
        }
      } else {
        message.warn('Debe ingresar al menos un campo');
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const searchEmpresa = async () => {
    try {
      setLoading(true);
      await form.validateFields(['qEmpresa']);
      const { qEmpresa } = form.getFieldsValue(['qEmpresa']);
      if (qEmpresa) {
        const _empresaFound = await getEmpresas({ q: qEmpresa });
        if (_empresaFound.results.length) {
          const [selected] = _empresaFound.results;
          form.setFieldsValue({
            empresa: selected.id,
          });
          setEmpresaFound([{
            ...selected,
            nombre_comercial: `${selected?.clave || ''} - ${selected?.nombre_comercial}`,
          }]);
        } else if (!_empresaFound.results.length) {
          message.info('Empresa no encontrada');
          setVisibleBusquedaAvanzada(true);
        }
      }
      setLoading(false);
    } catch (error) {
      onError(error, setLoading);
    }
  };

  const fetchEmpresa = async (id) => {
    try {
      setLoading(true);
      if (id) {
        const _empresa = await getEmpresa(id?.id || id);
        if (_empresa) {
          setEmpresaFound({
            ..._empresa,
            nombre_comercial: `${_empresa?.clave} - ${_empresa?.nombre_comercial}`,
          });
          setTimeout(() => {
            form.setFieldsValue({
              empresa: _empresa.id,
            });
          });
        } else {
          message.info('No se encontro la empresa');
        }
        setLoading(false);
      }
    } catch (error) {
      onError(error, setLoading);
    }
  };

  // eslint-disable-next-line react/prop-types
  const Formulario = () => {
    const modal = Boolean(!selectedRowKeys.length);
    const breakPoints = { xs: 24, sm: 24, md: modal ? 8 : 24 };
    return (
      <Form
        layout="vertical"
        form={form}
        onFinish={() => (!visibleBusquedaModal ? onFinish() : handleSearch())}
        scrollToFirstError
        initialValues={{ estados_globales: 1 }}
      >
        <Spin spinning={loading} tip="Cargando">
          {!visibleBusquedaModal ? (
            <Row gutter={10}>
              <InputSearchPro
                name="q"
                onClickAdvanced={() => (setVisibleBusquedaAvanzada(true))}
                label="Ciudadano"
                inputProps={{ onPressEnter: () => searchCiudadano() }}
                breakPoints={breakPoints}
                resultProps={{
                  breakPoints,
                  rules: rules.required,
                  name: 'ciudadano',
                  label: 'Ciudadano',
                  dataSource: ciudadanoFound,
                  onClear: resetAndValidate,
                  render: (e) => ` ${e.clave_ciudadana} - ${e.nombre_completo_corto}`,
                }}
              />
              <InputSearchPro
                name="qEmpresa"
                onClickAdvanced={() => (setVisibleBusquedaAvanzadaEmpresa(true))}
                label="Empresa"
                inputProps={{
                  onPressEnter: () => searchEmpresa(),
                }}
                breakPoints={breakPoints}
                resultProps={{
                  breakPoints,
                  name: 'empresa',
                  label: 'Empresa',
                  dataSource: empresaFound,
                  onClear: () => resetAndValidate('qEmpresa'),
                  labelProp: 'nombre_comercial',
                }}
              />
              <Uploader
                title="Identificación"
                limitMB={50}
                formItemName="documento_de_identificacion"
                allowedExtensions={['pdf', 'docx', 'doc']}
                fullSize={modal}
                allowPreview
              />
              <Uploader
                title="Oficio de Autorización"
                limitMB={50}
                formItemName="documento_autorizacion_de_oficio"
                allowedExtensions={['pdf', 'docx', 'doc']}
                fullSize={modal}
                allowPreview
              />
              <Uploader
                title="Oficio Poder"
                limitMB={50}
                formItemName="documento_oficio_poder"
                allowedExtensions={['pdf', 'docx', 'doc']}
                fullSize={modal}
                allowPreview
              />
              <Col span={24}>
                <Form.Item
                  name="observaciones_del_beneficiario"
                  label="Observaciones"
                  hasFeedback
                  normalize={capitalize}
                >
                  <Input.TextArea
                    autoSize={{ minRows: 4, maxRows: 8 }}
                    allowClear
                  />
                </Form.Item>
              </Col>

              {(!modal) && (
                <>
                  <Typography.Title level={3}>
                    Depósitos en garantía
                  </Typography.Title>
                  <DepositosEnGarantia registro_de_beneficiario={selectedRowKeys[0]} />
                </>
              )}
            </Row>
          ) : (
            <>
              <Row gutter={10}>
                <Col md={8} sm={24} xs={24}>
                  <Form.Item
                    label="Clave ciudadana"
                    name="clave_ciudadana"
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
                <Col md={8} sm={24} xs={24}>
                  <Form.Item
                    label="Nombre"
                    name="nombre"
                    normalize={capitalize}
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
                <Col md={8} sm={24} xs={24}>
                  <Form.Item
                    label="Apellido paterno"
                    name="apellido_paterno"
                    normalize={capitalize}
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
                <Col md={8} sm={24} xs={24}>
                  <Form.Item
                    label="Apellido materno"
                    name="apellido_materno"
                    normalize={capitalize}
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>

                <Col md={8} sm={24} xs={24}>
                  <Form.Item
                    label="Correo"
                    rules={rules.email}
                    name="email"
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
                <LadaNumero
                  form={form}
                  breakPoints={{
                    md: 24,
                    lg: 16,
                    xl: 8,
                    xxl: 8,
                  }}
                  names={{
                    lada: 'lada',
                    numero_de_telefono: 'numero_de_celular',
                  }}
                />
                <Col md={8} sm={24} xs={24}>
                  <Form.Item
                    label="CURP"
                    rules={rules.CURP}
                    name="CURP"
                    normalize={(value) => value.toUpperCase()}
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
                <Col md={8} sm={24} xs={24}>
                  <Form.Item
                    label="RFC"
                    rules={rules.RFC}
                    name="RFC"
                    normalize={(val) => val?.toUpperCase()}
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
              </Row>
              <Row justify="end" gutter={10}>
                <Col md={8} sm={24} xs={24}>
                  <Button
                    block
                    icon={<SearchOutlined />}
                    onClick={() => handleSearch()}
                  >
                    Buscar
                  </Button>
                </Col>
                <Col md={8} sm={24} xs={24}>
                  <Button
                    block
                    icon={<ShoppingCartOutlined />}
                    disabled={!selectedRowKeysCiudadanos.length || loading}
                    onClick={onAdd}
                  >
                    Cargar
                  </Button>
                </Col>
                <br />
                <Col span={24}>
                  <Table
                    handleOnRowClick={handleOnRowClickSearch}
                    rowSelection={rowSelectionCiudadanos}
                    data={dataCiudadanos.results}
                    cols={columnsBusqueda}
                    rowKey="id"
                    noControls
                    allowSort={false}
                    allowSearch={false}
                    mobileColIndex={0}
                    hasAllViewPermission
                    currentPage={currentPage}
                    count={dataCiudadanos.count}
                    paginationCallback={handleSearch}
                    pagination={false}
                  />
                </Col>
              </Row>
            </>
          )}
          <Form.Item hidden>
            <Button htmlType="submit" />
          </Form.Item>
        </Spin>
      </Form>
    );
  };

  return (
    <Row id="sort-rows" align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {(
          (!visible && !selectedRowKeys.length)
          || (!visible && selectedRowKeys.length)
          || (visible && !selectedRowKeys.length)) && (
            <Table
              cols={columns}
              data={data}
              rowSelection={rowSelection}
              handleOnRowClick={handleOnRowClick}
              controls={{
                onClickAdd,
                onClickEdit,
                onClickDelete,
              }}
              allowImport
              rowKey="id"
              permission={permission}
            />
        )}
        {(visible && !!selectedRowKeys.length) && (
          <Card
            className="form-container"
            title="Editar Registro de beneficiario"
            extra={(
              <FormSubmitControls
                onFinish={onFinish}
                onCancel={onCancel}
                permission={permission}
                loading={loading}
              />
            )}
            bordered={false}
          >
            <Formulario />
          </Card>
        )}
      </Spin>
      <Modal
        visible={visible && !selectedRowKeys.length}
        width={visibleBusquedaModal ? '80%' : 520}
        title={(
          <FormSubmitControls
            label={visibleBusquedaModal ? 'Buscar Ciudadano' : 'Agregar registro de beneficiario'}
            onFinish={() => onFinish()}
            onCancel={onCancel}
            loading={loading}
            permission={permission}
          />
        )}
        closable={false}
        onCancel={onCancel}
        footer={null}
        keyboard={!loading}
        maskClosable={!loading}
        forceRender
      >
        <Formulario modal />
      </Modal>
      <ModalDelete
        onDelete={deleteItem}
        onCancel={() => setVisibleAlert(false)}
        visible={visibleAlert}
        content="El registro de beneficiario seleccionado"
        loading={loading}
      />
      <BusquedaAvanzadaPadrones
        tipoDePadron={1}
        visible={visibleBusquedaAvanzada}
        setVisible={setVisibleBusquedaAvanzada}
        callback={(found) => {
          setCiudadanoFound([found]);
          form.setFieldsValue({
            ciudadano_publico_en_general: found.id,
            ciudadano: found.id,
          });
        }}
        allowAdd={false}
      />
      <BusquedaAvanzadaPadrones
        tipoDePadron={2}
        visible={visibleBusquedaAvanzadaEmpresa}
        setVisible={setVisibleBusquedaAvanzadaEmpresa}
        callback={(found) => fetchEmpresa(found)}
        allowAdd={false}
      />
    </Row>
  );
};

export default RegistroDeBeneficiarios;
